import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { header } from './header.encry';
import SquareImageCropModal from './SquareImageCropModal';
import {
  getProductsByCategory,
  syncOrders as syncOrdersPortal,
  syncProductsByCategory,
  createProductPortal,
  checkCommandStatus,
  syncAddonsPortal,
  uploadImageToGlovo,
  syncCategoriesPortal,
  syncReviews,
  fetchAllReviews,
  toggleProduct,
  deleteProduct,
} from './GlovoSistersPortalService';

const API_URL = process.env.REACT_APP_PEGASUS_API_URL;

const normalizeId = (value) => {
  if (!value) return '';
  if (typeof value === 'object') {
    return String(value.$oid || value._id || value.id || '').trim();
  }
  return String(value).trim();
};

const getCategoryCatalogId = (category) => (
  category?._catalogId ||
  category?.catalog_id ||
  category?.catalogId ||
  category?.catalog?.id ||
  category?.catalog?._id ||
  ''
);

const getCategoryStoreRefs = (category) => {
  const refs = [
    category?.store_id,
    category?.storeId,
    category?.store,
    category?.store_code,
    category?.storeCode,
    category?.vendor_id,
    category?.vendorId,
    category?.remote_store_id,
    category?.remoteStoreId,
  ];

  if (Array.isArray(category?.store_ids)) refs.push(...category.store_ids);
  if (Array.isArray(category?.storeIds)) refs.push(...category.storeIds);
  if (Array.isArray(category?.catalogIds)) refs.push(...category.catalogIds);
  if (Array.isArray(category?.catalog_ids)) refs.push(...category.catalog_ids);

  const catalogId = getCategoryCatalogId(category);
  if (catalogId) refs.push(catalogId);

  return refs.map(normalizeId).filter(Boolean);
};

const GlovoSistersServiceManager = ({ plateformId, selectedPlateform, onClose }) => {
  const [activeService, setActiveService] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  
  // Common parameters
  const [globalEntityId, setGlobalEntityId] = useState('GV_MA');
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [selectedVendorCodes, setSelectedVendorCodes] = useState([]);
  
  // Category parameters
  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [catalogId, setCatalogId] = useState('');
  
  // Product parameters
  const [productName, setProductName] = useState('');
  const [productDescription, setProductDescription] = useState('');
  const [productPrice, setProductPrice] = useState('');
  const [productImageUrl, setProductImageUrl] = useState('');
  const [productAvailable, setProductAvailable] = useState(true);
  const [productOptionGroupIds, setProductOptionGroupIds] = useState('');
  const [productId, setProductId] = useState('');
  
  // Product creation workflow
  const [productImageFile, setProductImageFile] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [productFieldsEnabled, setProductFieldsEnabled] = useState(false);
  const [showCropModal, setShowCropModal] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [selectedCatalogs, setSelectedCatalogs] = useState([]);
  const [catalogStoresMap, setCatalogStoresMap] = useState({});
  const [catalogCategoriesMap, setCatalogCategoriesMap] = useState({});
  
  // Command parameters
  const [commandId, setCommandId] = useState('');
  
  // Image parameters
  
  // Review parameters
  const [perPage, setPerPage] = useState(50);
  const [hasText, setHasText] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  
  // Pagination for categories
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [fetchingProductsForCategory, setFetchingProductsForCategory] = useState(null);
  
  // Products modal
  const [showProductsModal, setShowProductsModal] = useState(false);
  const [modalProducts, setModalProducts] = useState([]);
  const [modalCategoryName, setModalCategoryName] = useState('');
  
  // Queue sync all categories
  const [isSyncingAllCategories, setIsSyncingAllCategories] = useState(false);
  const [syncAllProgress, setSyncAllProgress] = useState({ current: 0, total: 0, results: [] });
  
  // Orders sync
  const [ordersSyncResult, setOrdersSyncResult] = useState(null);
  const [isAutoSyncEnabled, setIsAutoSyncEnabled] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  
  // Stores for selection (filtered by platform)
  const [platformStores, setPlatformStores] = useState([]);

  // Get vendorId from selected store's storeCode
  const getVendorId = useCallback(() => {
    if (!selectedStoreId) return '';
    
    const selectedStore = platformStores.find(s => (s._id || s.id) === selectedStoreId);
    if (selectedStore) {
      // Prefer mapped remote_store_id when available (vendors mapping)
      if (selectedStore.remote_store_id) return selectedStore.remote_store_id;
      if (selectedStore.storeCode) return selectedStore.storeCode;
    }
    
    return '';
  }, [platformStores, selectedStoreId]);

  const vendorId = getVendorId();

  // Sync orders from Glovo Portal
  const syncOrders = async ({ plateformId, vendorId, globalEntityId = 'GV_MA', timeFrom, timeTo }) => {
    const now = new Date();
    const fiveMinutesAgo = new Date(now.getTime() - 5 * 60 * 1000);

    const payload = {
      plateformId,
      globalEntityId,
      timeFrom: timeFrom || fiveMinutesAgo.toISOString(),
      timeTo: timeTo || now.toISOString(),
    };

    if (vendorId) {
      payload.vendorId = vendorId;
    }

    return syncOrdersPortal(payload);
  };

  // Auto-sync orders every 5 minutes
  useEffect(() => {
    if (!isAutoSyncEnabled || !plateformId) return;

    const syncInterval = setInterval(async () => {
      try {
        const currentVendorId = getVendorId();
        const response = await syncOrders({
          plateformId,
          vendorId: currentVendorId || undefined,
          globalEntityId,
        });
        setOrdersSyncResult(response);
        setLastSyncTime(new Date());
      } catch (err) {
        console.error('Auto-sync error:', err);
      }
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(syncInterval);
  }, [getVendorId, globalEntityId, isAutoSyncEnabled, plateformId]);

  // Clear selected category when store changes
  useEffect(() => {
    setSelectedCategoryId('');
    setCatalogId('');
  }, [selectedStoreId]);

  const loadCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/productsCategories`, header());
      const allCategories = response.data.response || response.data;
      const glovoCategories = allCategories.filter(cat => 
        String(cat.type || '').toLowerCase() === 'glovo_sisters' &&
        normalizeId(cat.plateform_id) === normalizeId(plateformId)
      );
      setCategories(glovoCategories);
    } catch (err) {
      console.error('Error loading categories:', err);
    }
  }, [plateformId]);

  // Filter categories by selected store
  const filteredCategories = selectedStoreId 
    ? categories.filter(cat => {
        const selectedStore = platformStores.find(s => normalizeId(s._id || s.id) === normalizeId(selectedStoreId));
        const acceptedRefs = [
          selectedStoreId,
          selectedStore?._id,
          selectedStore?.id,
          selectedStore?.storeCode,
          selectedStore?.remote_store_id,
          selectedStore?.remoteStoreId,
        ].map(normalizeId).filter(Boolean);
        const categoryRefs = getCategoryStoreRefs(cat);
        return categoryRefs.length === 0 || categoryRefs.some(ref => acceptedRefs.includes(ref));
      })
    : categories;

  const loadStores = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/stores`, header());
      const stores = response.data.response || response.data;
      
      // Filter stores that belong to this platform
      if (selectedPlateform?.store_ids && selectedPlateform.store_ids.length > 0) {
        const filtered = stores.filter(store => 
          selectedPlateform.store_ids.some(id => 
            (id.$oid || id) === (store._id || store.id)
          )
        );
        // Attach remote_store_id mapping from selectedPlateform.additionnal_data.stores_maped
        const mapping = selectedPlateform.additionnal_data?.stores_maped;

        const mapped = filtered.map(store => {
          const storeId = store._id || store.id;
          let remoteId = null;
          if (mapping) {
            if (Array.isArray(mapping)) {
              const m = mapping.find(m => m.store_id === storeId || (m.store_id && (m.store_id.$oid || m.store_id) === storeId));
              if (m) remoteId = m.remote_store_id || m.vendorId || m.vendor_id;
            } else if (typeof mapping === 'object') {
              if (mapping.store_id === storeId || (mapping.store_id && (mapping.store_id.$oid || mapping.store_id) === storeId)) {
                remoteId = mapping.remote_store_id || mapping.vendorId || mapping.vendor_id;
              }
            }
          }

          return { ...store, remote_store_id: remoteId || store.storeCode || store.global_vendor_id || null };
        });

        setPlatformStores(mapped);
        
        // Auto-select first store if available
        if (filtered.length > 0 && !selectedStoreId) {
          setSelectedStoreId(mapped[0]._id || mapped[0].id);
        }
      }
    } catch (err) {
      console.error('Error loading stores:', err);
    }
  }, [selectedPlateform, selectedStoreId]);

  useEffect(() => {
    if (plateformId && selectedPlateform) {
      loadCategories();
      loadStores();
    }
  }, [loadCategories, loadStores, plateformId, selectedPlateform]);

  // Load distinct catalog IDs and group stores/categories
  useEffect(() => {
    if (categories.length > 0 && platformStores.length > 0) {
      // Get distinct catalog IDs
      const catalogMap = {};
      const catMap = {};
      
      categories.forEach(cat => {
        const catId = cat._catalogId || cat.catalog_id || cat.catalogId;
        if (catId) {
          if (!catalogMap[catId]) {
            catalogMap[catId] = [];
          }
          if (!catMap[catId]) {
            catMap[catId] = [];
          }
          
          // Add store if not already in list
          const storeId = cat.store_id;
          if (storeId && !catalogMap[catId].includes(storeId)) {
            catalogMap[catId].push(storeId);
          }
          
          // Add category
          if (!catMap[catId].find(c => (c.id || c.remote_plateform_id) === (cat.id || cat.remote_plateform_id))) {
            catMap[catId].push(cat);
          }
        }
      });
      
      setCatalogStoresMap(catalogMap);
      setCatalogCategoriesMap(catMap);
    }
  }, [categories, platformStores]);

  const handleProductImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Check if it's an image
    if (!file.type.startsWith('image/')) {
      alert('Veuillez sélectionner une image');
      return;
    }
    
    // Store the file but don't upload yet
    setProductImageFile(file);
    
    // Create image to check dimensions
    const img = new Image();
    const reader = new FileReader();
    
    reader.onload = (event) => {
      img.src = event.target.result;
      
      img.onload = () => {
        const isSquare = img.width === img.height;
        const is1000x1000 = img.width === 1000 && img.height === 1000;
        
        if (!isSquare || !is1000x1000) {
          // Open crop modal
          setImageToCrop(event.target.result);
          setShowCropModal(true);
        }
      };
    };
    
    reader.readAsDataURL(file);
  };

  const handleCroppedImage = (croppedBlob) => {
    setShowCropModal(false);
    setProductImageFile(croppedBlob);
    setImageToCrop(null);
  };

  const handleUploadProductImage = async () => {
    if (!productImageFile) {
      alert('Veuillez sélectionner une image d\'abord');
      return;
    }
    
    setUploadingImage(true);
    try {
      const response = await uploadImageToGlovo({
        plateformId,
        vendorId,
        image: productImageFile,
        globalEntityId,
        itemType: 'PRODUCT',
      });
      
      if (response.url || response.imageUrl) {
        setProductImageUrl(response.url || response.imageUrl);
        setProductFieldsEnabled(true);
        alert('Image uploadée avec succès!');
      } else {
        throw new Error('URL d\'image non retournée par le serveur');
      }
    } catch (err) {
      console.error('Error uploading image:', err);
      alert(`Erreur lors de l'upload de l'image: ${err?.response?.data?.error || err.message}`);
    } finally {
      setUploadingImage(false);
    }
  };

  const resetForm = () => {
    setResult(null);
    setError(null);
  };

  const normalizeProduct = async (product, platformType = 'glovo') => {
    try {
      const response = await axios.post(
        `${API_URL}/godroid/products/normalize`,
        {
          remote_id: product.id,
          platform: platformType,
          plateform_id: plateformId,
          store_id: selectedStoreId
        },
        header()
      );
      
      if (response.data.found && response.data.product) {
        return {
          ...product,
          product_id: response.data.product._id,
          local_product: response.data.product
        };
      }
      
      return product;
    } catch (err) {
      console.error('Error normalizing product:', product.id, err);
      return product;
    }
  };

  const handleFetchProductsForCategory = async (category) => {
    setFetchingProductsForCategory(category.id || category.remote_plateform_id);
    
    const catalogIdValue = category._catalogId || category.catalog_id || category.catalogId;
    
    if (!catalogIdValue) {
      alert(`Erreur: Catalog ID manquant pour la catégorie "${category.name}". Veuillez synchroniser les catégories d'abord.`);
      setFetchingProductsForCategory(null);
      return;
    }
    
    try {
      const response = await getProductsByCategory({
        plateformId,
        vendorId,
        catalogId: catalogIdValue,
        categoryId: category.id || category.remote_plateform_id,
        globalEntityId,
      });
      
      // Normalize products to get local product_id
      const normalizedProducts = await Promise.all(
        (response.products || []).map(product => normalizeProduct(product, 'glovo'))
      );
      
      // Update the category with fetched and normalized products
      setResult(prevResult => ({
        ...prevResult,
        categories: prevResult.categories.map(cat => {
          if ((cat.id || cat.remote_plateform_id) === (category.id || category.remote_plateform_id)) {
            return {
              ...cat,
              products: normalizedProducts,
              productsCount: response.totalProducts || normalizedProducts.length || 0
            };
          }
          return cat;
        })
      }));
      
      // Open modal with products
      setModalProducts(normalizedProducts);
      setModalCategoryName(category.name);
      setShowProductsModal(true);
    } catch (err) {
      console.error('Error fetching products for category:', err);
      alert(`Erreur lors de la récupération des produits: ${err?.response?.data?.error || err.message}`);
    } finally {
      setFetchingProductsForCategory(null);
    }
  };

  const handleToggleProduct = async (product) => {
    const selectedStore = platformStores.find(s => (s._id || s.id) === selectedStoreId);
    const storeCode = selectedStore?.storeCode || '';
    
    if (!storeCode) {
      setError('Veuillez sélectionner un magasin pour activer/désactiver le produit');
      return;
    }

    try {
      const newAvailability = !product.availability?.available;
      
      await toggleProduct({
        plateformId,
        sku: product.id,
        active: newAvailability,
        storeCode,
        product_id: product.product_id || null,
      });

      // Update the product in the results and modal
      const updateProducts = (products) => 
        products.map(p =>
          p.id === product.id
            ? {
                ...p,
                availability: {
                  ...p.availability,
                  available: newAvailability,
                },
              }
            : p
        );
      
      setResult(prevResult => ({
        ...prevResult,
        products: prevResult.products ? updateProducts(prevResult.products) : prevResult.products,
      }));
      
      setModalProducts(prevProducts => updateProducts(prevProducts));
    } catch (err) {
      console.error('Error toggling product:', err);
      setError(err?.response?.data?.error || err?.response?.data?.message || err.message);
    }
  };

  const handleSyncAllCategories = async () => {
    if (!selectedStoreId) {
      alert('Veuillez sélectionner un magasin d\'abord');
      return;
    }

    if (!window.confirm(`Êtes-vous sûr de vouloir synchroniser toutes les catégories (${filteredCategories.length}) pour ce magasin?`)) {
      return;
    }

    setIsSyncingAllCategories(true);
    setSyncAllProgress({ current: 0, total: filteredCategories.length, results: [] });
    setError(null);

    const results = [];
    const selectedStore = platformStores.find(s => (s._id || s.id) === selectedStoreId);
    // Prefer mapped remote_store_id (vendor id) over storeCode for bulk sync
    const vendorIdentifier = selectedStore?.remote_store_id || selectedStore?.storeCode || '';
    const storeCodeIdentifier = selectedStore?.storeCode || vendorIdentifier;

    for (let i = 0; i < filteredCategories.length; i++) {
      const category = filteredCategories[i];
      setSyncAllProgress(prev => ({ ...prev, current: i + 1 }));

        try {
        const response = await syncProductsByCategory({
          plateformId,
          vendorId: vendorIdentifier,
          categoryId: category.remote_plateform_id || category.id,
          globalEntityId,
          storeCode: storeCodeIdentifier,
          catalogId: getCategoryCatalogId(category) || null,
        });

        results.push({
          category: category.name,
          categoryId: category.remote_plateform_id || category.id,
          success: true,
          data: response,
        });
      } catch (err) {
        console.error(`Error syncing category ${category.name}:`, err);
        results.push({
          category: category.name,
          categoryId: category.remote_plateform_id || category.id,
          success: false,
          error: err?.response?.data?.error || err?.response?.data?.message || err.message,
        });
      }

      // Small delay between requests to avoid overwhelming the server
      if (i < filteredCategories.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }

    setSyncAllProgress(prev => ({ ...prev, results }));
    setIsSyncingAllCategories(false);

    // Show summary
    const successful = results.filter(r => r.success).length;
    const failed = results.filter(r => !r.success).length;
    alert(`Synchronisation terminée!\n\nRéussi: ${successful}\nÉchoué: ${failed}\nTotal: ${results.length}`);
  };

  const handleDeleteProduct = async (product) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer "${product.name}" ?`)) {
      return;
    }

    setLoading(true);
    try {
      const response = await deleteProduct({
        plateformId,
        vendorId,
        productId: product.id,
        globalEntityId,
        product_id: product.product_id || null,
      });

      // Remove product from the list and modal
      setResult(prevResult => ({
        ...prevResult,
        products: prevResult.products ? prevResult.products.filter(p => p.id !== product.id) : prevResult.products,
        totalProducts: prevResult.totalProducts ? prevResult.totalProducts - 1 : (prevResult.products?.length || 0) - 1,
      }));
      
      setModalProducts(prevProducts => prevProducts.filter(p => p.id !== product.id));

      // Show success message with command ID
      if (response.commandId) {
        alert(`Produit supprimé avec succès!\nCommand ID: ${response.commandId}`);
      }
    } catch (err) {
      console.error('Error deleting product:', err);
      setError(err?.response?.data?.error || err?.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleExecuteService = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    setCurrentPage(1); // Reset pagination

    try {
      let response;
      
      // Get storeCode from selected store
      const selectedStore = platformStores.find(s => (s._id || s.id) === selectedStoreId);
      const storeCode = selectedStore?.storeCode || '';
      
      // Get vendor codes for multi-select
      const vendorCodes = selectedVendorCodes.length > 0
        ? selectedVendorCodes.map(codeOrId => {
            // If the selected value is a store _id, resolve to its remote_store_id/storeCode
            const store = platformStores.find(s => (s._id || s.id) === codeOrId);
            if (store) return store.remote_store_id || store.storeCode;
            // Otherwise assume the value is already a remote vendor id
            return codeOrId;
          }).filter(Boolean)
        : undefined;

      switch (activeService) {
        case 'getProductsByCategory':
          response = await getProductsByCategory({
            plateformId,
            vendorId,
            catalogId,
            categoryId: selectedCategoryId,
            globalEntityId,
          });
          
          // Normalize products to get local product_id
          if (response.products && Array.isArray(response.products)) {
            response.products = await Promise.all(
              response.products.map(product => normalizeProduct(product, 'glovo'))
            );
          }
          break;

        case 'syncProductsByCategory':
          response = await syncProductsByCategory({
            plateformId,
            vendorId,
            categoryId: selectedCategoryId,
            globalEntityId,
            storeCode: storeCode || vendorId || null,
            catalogId: catalogId || null,
          });
          break;

        case 'createProductPortal':
          // Validate required fields
          if (!productImageUrl) {
            throw new Error('Veuillez d\'abord uploader une image');
          }
          if (!productName || productName.length > 150) {
            throw new Error('Le nom du produit est requis (max 150 caractères)');
          }
          if (!productDescription || productDescription.length > 250) {
            throw new Error('La description est requise (max 250 caractères)');
          }
          if (!productPrice || parseFloat(productPrice) < 5) {
            throw new Error('Le prix minimum est de 5 MAD');
          }
          if (selectedCatalogs.length === 0) {
            throw new Error('Veuillez sélectionner au moins un catalogue');
          }
          if (!selectedCategoryId) {
            throw new Error('Veuillez sélectionner une catégorie');
          }
          
          // Create product for each selected catalog
          const results = [];
          for (const catId of selectedCatalogs) {
            try {
              const result = await createProductPortal({
                plateformId,
                vendorId,
                globalEntityId,
                productId: productId || null,
                productData: {
                  name: productName,
                  description: productDescription,
                  price: parseFloat(productPrice) * 100, // Convert to cents
                  categoryId: selectedCategoryId,
                  imageUrl: productImageUrl,
                  available: productAvailable,
                  catalogIds: selectedCatalogs,
                  productOptionGroupIds: productOptionGroupIds 
                    ? productOptionGroupIds.split(',').map(s => s.trim())
                    : [],
                },
              });
              results.push({ catalogId: catId, success: true, result });
            } catch (err) {
              results.push({ catalogId: catId, success: false, error: err.message });
            }
          }
          
          response = {
            totalCatalogs: selectedCatalogs.length,
            successful: results.filter(r => r.success).length,
            failed: results.filter(r => !r.success).length,
            results
          };
          break;

        case 'checkCommandStatus':
          response = await checkCommandStatus({
            plateformId,
            vendorId,
            commandId,
            globalEntityId,
            productId: productId || null,
          });
          break;

        case 'syncAddonsPortal':
          response = await syncAddonsPortal({
            plateformId,
            vendorId,
            globalEntityId,
            storeId: selectedStoreId || null,
          });
          break;

        case 'syncCategoriesPortal':
          response = await syncCategoriesPortal({
            plateformId,
            vendorId,
            globalEntityId,
            storeId: selectedStoreId || null,
          });
          await loadCategories();
          break;

        case 'syncReviews':
          response = await syncReviews({
            plateformId,
            storeCode,
            perPage,
          });
          break;

        case 'fetchAllReviews':
          response = await fetchAllReviews({
            plateformId,
            globalVendorCodes: vendorCodes,
            fromDate: fromDate || undefined,
            toDate: toDate || undefined,
            perPage,
            hasText,
          });
          break;

        case 'syncOrders':
          response = await syncOrders({
            plateformId,
            vendorId: vendorId || undefined,
            globalEntityId,
          });
          setOrdersSyncResult(response);
          setLastSyncTime(new Date());
          break;

        default:
          throw new Error('Service non reconnu');
      }

      setResult(response);
    } catch (err) {
      console.error('Error executing service:', err);
      setError(err?.response?.data?.error || err?.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const services = [
    {
      id: 'products',
      name: 'Gestion des Produits',
      items: [
        {
          id: 'getProductsByCategory',
          name: 'Obtenir les produits par catégorie',
          description: 'Récupère tous les produits d\'une catégorie depuis Glovo Portal',
          params: ['plateformId', 'vendorId', 'catalogId', 'categoryId', 'globalEntityId'],
        },
        {
          id: 'syncProductsByCategory',
          name: 'Synchroniser les produits par catégorie',
          description: 'Synchronise les produits d\'une catégorie avec la base de données locale',
          params: ['plateformId', 'vendorId', 'categoryId', 'globalEntityId', 'storeCode'],
        },
        {
          id: 'createProductPortal',
          name: 'Créer un produit',
          description: 'Crée un nouveau produit dans Glovo Portal',
          params: ['plateformId', 'vendorId', 'productData', 'globalEntityId', 'productId'],
        },
        {
          id: 'checkCommandStatus',
          name: 'Vérifier le statut de création',
          description: 'Vérifie le statut d\'une commande de création de produit',
          params: ['plateformId', 'vendorId', 'commandId', 'globalEntityId', 'productId'],
        },
      ],
    },
    {
      id: 'categories',
      name: 'Gestion des Catégories',
      items: [
        {
          id: 'syncCategoriesPortal',
          name: 'Synchroniser les catégories',
          description: 'Synchronise toutes les catégories depuis Glovo Portal',
          params: ['plateformId', 'vendorId', 'globalEntityId', 'storeId'],
        },
      ],
    },
    {
      id: 'addons',
      name: 'Gestion des Addons',
      items: [
        {
          id: 'syncAddonsPortal',
          name: 'Synchroniser les addons',
          description: 'Synchronise les addons/options depuis Glovo Portal avec matching intelligent par plateformId + remote_id ou par nom. Ajoute automatiquement des enregistrements de plateforme aux addons existants trouvés par nom.',
          params: ['plateformId', 'vendorId', 'globalEntityId', 'storeId'],
        },
      ],
    },
    {
      id: 'reviews',
      name: 'Gestion des Avis',
      items: [
        {
          id: 'syncReviews',
          name: 'Synchroniser les avis (legacy)',
          description: 'Synchronise les avis clients',
          params: ['plateformId', 'storeCode', 'perPage'],
        },
        {
          id: 'fetchAllReviews',
          name: 'Récupérer tous les avis',
          description: 'Récupère tous les avis avec pagination automatique',
          params: ['plateformId', 'globalVendorCodes', 'fromDate', 'toDate', 'perPage', 'hasText'],
        },
      ],
    },
    {
      id: 'orders',
      name: 'Gestion des Commandes',
      items: [
        {
          id: 'syncOrders',
          name: 'Synchroniser les commandes',
          description: 'Synchronise les commandes Glovo (par défaut: dernières 5 minutes)',
          params: ['plateformId', 'vendorId', 'globalEntityId'],
        },
      ],
    },
  ];

  const renderParameterInputs = () => {
    if (!activeService) return null;

    const service = services
      .flatMap(group => group.items)
      .find(item => item.id === activeService);

    if (!service) return null;

    return (
      <div className="mb-3">
        <h6 className="mb-3">Paramètres requis</h6>
        
        {/* Store Selection - MUST BE FIRST */}
        {(service.params.includes('storeCode') || service.params.includes('storeId') || service.params.includes('categoryId')) && (
          <div className="mb-3">
            <label className="form-label">Magasin *</label>
            <Select
              options={platformStores.map(store => ({
                value: store._id || store.id,
                label: `${store.name} (${store.storeCode})`,
                storeCode: store.storeCode
              }))}
              value={platformStores
                .map(store => ({
                  value: store._id || store.id,
                  label: `${store.name} (${store.storeCode})`,
                  storeCode: store.storeCode
                }))
                .find(opt => opt.value === selectedStoreId) || null}
              onChange={(selected) => setSelectedStoreId(selected?.value || '')}
              placeholder="Sélectionner un magasin"
              isClearable
              isSearchable
              required
            />
            <small className="text-muted">
              {selectedStoreId && (
                <>
                  VendorId (remote): {platformStores.find(s => (s._id || s.id) === selectedStoreId)?.remote_store_id || platformStores.find(s => (s._id || s.id) === selectedStoreId)?.storeCode || 'N/A'}
                </>
              )}
              {!selectedStoreId && 'Sélectionnez d\'abord un magasin'}
            </small>
          </div>
        )}

        {/* Category Parameters */}
        {service.params.includes('categoryId') && (
          <div className="mb-3">
            <label className="form-label">Catégorie *</label>
            {!selectedStoreId && (
              <div className="alert alert-warning mb-2">
                <small>⚠️ Veuillez d'abord sélectionner un magasin pour filtrer les catégories</small>
              </div>
            )}
            <Select
              options={filteredCategories.map(cat => ({
                value: cat.remote_plateform_id || cat.id,
                label: `${cat.name} (${cat.remote_plateform_id || cat.id})`,
                catalogId: getCategoryCatalogId(cat)
              }))}
              value={filteredCategories
                .map(cat => ({
                  value: cat.remote_plateform_id || cat.id,
                  label: `${cat.name} (${cat.remote_plateform_id || cat.id})`,
                  catalogId: getCategoryCatalogId(cat)
                }))
                .find(opt => opt.value === selectedCategoryId) || null}
              onChange={(selected) => {
                setSelectedCategoryId(selected?.value || '');
                if (selected?.catalogId) {
                  setCatalogId(selected.catalogId);
                }
              }}
              placeholder={selectedStoreId ? "Sélectionner une catégorie" : "Sélectionnez d'abord un magasin"}
              isClearable
              isSearchable
              isDisabled={!selectedStoreId || isSyncingAllCategories}
              required
            />
            {selectedStoreId && filteredCategories.length === 0 && (
              <small className="text-muted">Aucune catégorie disponible pour ce magasin</small>
            )}
            
            {/* Sync All Categories Button */}
            {activeService === 'syncProductsByCategory' && selectedStoreId && filteredCategories.length > 0 && (
              <div className="mt-3">
                <button
                  type="button"
                  className="btn btn-warning w-100"
                  onClick={handleSyncAllCategories}
                  disabled={isSyncingAllCategories || loading}
                >
                  {isSyncingAllCategories ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Synchronisation en cours... ({syncAllProgress.current}/{syncAllProgress.total})
                    </>
                  ) : (
                    <>
                      <i className="fas fa-sync-alt me-2"></i>
                      Synchroniser toutes les catégories ({filteredCategories.length})
                    </>
                  )}
                </button>
                <small className="text-muted d-block mt-2">
                  <i className="fas fa-info-circle me-1"></i>
                  Cette action synchronisera automatiquement toutes les catégories du magasin sélectionné.
                </small>
              </div>
            )}
          </div>
        )}

        {service.params.includes('catalogId') && (
          <div className="mb-3">
            <label className="form-label">Catalog ID *</label>
            <input
              type="text"
              className="form-control"
              value={catalogId}
              onChange={(e) => setCatalogId(e.target.value)}
              placeholder="46330728"
              required
            />
          </div>
        )}

        {/* Product Creation Parameters */}
        {service.params.includes('productData') && (
          <>
            {/* Step 1: Image Selection */}
            <div className="mb-3">
              <label className="form-label">Image du produit * (1000x1000px recommandé)</label>
              <input
                type="file"
                className="form-control"
                accept="image/jpeg,image/png"
                onChange={handleProductImageSelect}
                disabled={uploadingImage || productImageUrl}
              />
              {productImageFile && !productImageUrl && (
                <div className="mt-2">
                  <img 
                    src={URL.createObjectURL(productImageFile)} 
                    alt="Preview" 
                    style={{ maxWidth: '200px', maxHeight: '200px' }} 
                    className="img-thumbnail" 
                  />
                  <div className="mt-2">
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={handleUploadProductImage}
                      disabled={uploadingImage}
                    >
                      {uploadingImage ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-1"></span>
                          Upload en cours...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-upload me-1"></i>
                          Upload to Glovo
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
              {productImageUrl && (
                <div className="mt-2">
                  <img src={productImageUrl} alt="Preview" style={{ maxWidth: '200px', maxHeight: '200px' }} className="img-thumbnail" />
                  <small className="text-success d-block">✓ Image uploadée avec succès</small>
                </div>
              )}
            </div>

            {/* Step 2: Select Catalogs */}
            {productFieldsEnabled && (
              <>
                <div className="mb-3">
                  <label className="form-label">Sélectionner les catalogues *</label>
                  {Object.keys(catalogStoresMap).length === 0 ? (
                    <div className="alert alert-warning">
                      Aucun catalogue trouvé. Veuillez synchroniser les catégories d'abord.
                    </div>
                  ) : (
                    <div className="list-group">
                      {Object.keys(catalogStoresMap).map(catId => {
                        const storeIds = catalogStoresMap[catId];
                        const stores = platformStores.filter(s => storeIds.includes(s._id || s.id));
                        return (
                          <div key={catId} className="list-group-item">
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={`catalog-${catId}`}
                                checked={selectedCatalogs.includes(catId)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedCatalogs([...selectedCatalogs, catId]);
                                  } else {
                                    setSelectedCatalogs(selectedCatalogs.filter(c => c !== catId));
                                  }
                                }}
                              />
                              <label className="form-check-label" htmlFor={`catalog-${catId}`}>
                                <strong>Catalog ID: {catId}</strong>
                                <small className="d-block text-muted">
                                  Magasins: {stores.map(s => s.name).join(', ')}
                                </small>
                              </label>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Categories per selected catalog */}
                {selectedCatalogs.map(catId => (
                  <div key={`cat-${catId}`} className="mb-3">
                    <label className="form-label">Catégorie pour Catalog {catId} *</label>
                    <Select
                      options={(catalogCategoriesMap[catId] || []).map(cat => ({
                        value: cat.id || cat.remote_plateform_id,
                        label: cat.name
                      }))}
                      onChange={(selected) => {
                        setSelectedCategoryId(selected?.value || '');
                      }}
                      placeholder="Sélectionner une catégorie"
                      isClearable
                      isSearchable
                    />
                  </div>
                ))}

                <div className="mb-3">
                  <label className="form-label">Nom du produit * (max 150 caractères)</label>
                  <input
                    type="text"
                    className="form-control"
                    value={productName}
                    onChange={(e) => setProductName(e.target.value.slice(0, 150))}
                    maxLength={150}
                    required
                    disabled={!productFieldsEnabled}
                  />
                  <small className="text-muted">{productName.length}/150</small>
                </div>
                
                <div className="mb-3">
                  <label className="form-label">Description (max 250 caractères)</label>
                  <textarea
                    className="form-control"
                    value={productDescription}
                    onChange={(e) => setProductDescription(e.target.value.slice(0, 250))}
                    maxLength={250}
                    rows={3}
                    disabled={!productFieldsEnabled}
                  />
                  <small className="text-muted">{productDescription.length}/250</small>
                </div>
                
                <div className="mb-3">
                  <label className="form-label">Prix (MAD) * (minimum 5 MAD)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="5"
                    className="form-control"
                    value={productPrice}
                    onChange={(e) => setProductPrice(e.target.value)}
                    required
                    disabled={!productFieldsEnabled}
                  />
                  {parseFloat(productPrice) < 5 && productPrice && (
                    <small className="text-danger">Le prix minimum est de 5 MAD</small>
                  )}
                </div>
                
                <div className="mb-3 form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="productAvailable"
                    checked={productAvailable}
                    onChange={(e) => setProductAvailable(e.target.checked)}
                    disabled={!productFieldsEnabled}
                  />
                  <label className="form-check-label" htmlFor="productAvailable">
                    Produit disponible
                  </label>
                </div>
                
                <div className="mb-3">
                  <label className="form-label">IDs des groupes d'options (séparés par virgule)</label>
                  <input
                    type="text"
                    className="form-control"
                    value={productOptionGroupIds}
                    onChange={(e) => setProductOptionGroupIds(e.target.value)}
                    placeholder="addon_id_1, addon_id_2"
                    disabled={!productFieldsEnabled}
                  />
                </div>
              </>
            )}
          </>
        )}

        {service.params.includes('productId') && activeService !== 'createProductPortal' && (
          <div className="mb-3">
            <label className="form-label">Product ID local (optionnel)</label>
            <input
              type="text"
              className="form-control"
              value={productId}
              onChange={(e) => setProductId(e.target.value)}
              placeholder="MongoDB ObjectId"
            />
          </div>
        )}

        {/* Command Parameters */}
        {service.params.includes('commandId') && (
          <div className="mb-3">
            <label className="form-label">Command ID *</label>
            <input
              type="text"
              className="form-control"
              value={commandId}
              onChange={(e) => setCommandId(e.target.value)}
              placeholder="cmd_123456789"
              required
            />
          </div>
        )}

        {/* Review Parameters */}
        {service.params.includes('perPage') && (
          <div className="mb-3">
            <label className="form-label">Nombre par page</label>
            <input
              type="number"
              className="form-control"
              value={perPage}
              onChange={(e) => setPerPage(parseInt(e.target.value))}
              min={1}
              max={100}
            />
          </div>
        )}

        {service.params.includes('hasText') && (
          <div className="mb-3 form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="hasText"
              checked={hasText}
              onChange={(e) => setHasText(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="hasText">
              Uniquement les avis avec texte
            </label>
          </div>
        )}

        {service.params.includes('fromDate') && (
          <div className="mb-3">
            <label className="form-label">Date de début</label>
            <input
              type="datetime-local"
              className="form-control"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>
        )}

        {service.params.includes('toDate') && (
          <div className="mb-3">
            <label className="form-label">Date de fin</label>
            <input
              type="datetime-local"
              className="form-control"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>
        )}

        {service.params.includes('globalVendorCodes') && (
          <div className="mb-3">
            <label className="form-label">Magasins (Vendor Codes)</label>
            <select
              className="form-select"
              multiple
              value={selectedVendorCodes}
              onChange={(e) => setSelectedVendorCodes(Array.from(e.target.selectedOptions, option => option.value))}
              size={Math.min(platformStores.length, 5)}
            >
              {platformStores.map(store => (
                <option key={store._id || store.id} value={store.remote_store_id || store.storeCode || (store._id || store.id)}>
                  {store.name} ({store.remote_store_id || store.storeCode || ''})
                </option>
              ))}
            </select>
            <small className="text-muted">
              Maintenez Ctrl/Cmd pour sélectionner plusieurs magasins. Laissez vide pour tous les magasins.
            </small>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="container-fluid">
      <div className="row">
        {/* Services List */}
        <div className="col-md-4 border-end" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
          <h5 className="mb-3">Services Disponibles</h5>
          {services.map(group => (
            <div key={group.id} className="mb-4">
              <h6 className="text-primary">{group.name}</h6>
              <div className="list-group">
                {group.items.map(service => (
                  <button
                    key={service.id}
                    className={`list-group-item list-group-item-action ${activeService === service.id ? 'active' : ''}`}
                    onClick={() => {
                      setActiveService(service.id);
                      resetForm();
                    }}
                  >
                    <div className="d-flex w-100 justify-content-between">
                      <h6 className="mb-1">{service.name}</h6>
                    </div>
                    <p className="mb-1 small">{service.description}</p>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Service Execution */}
        <div className="col-md-8" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
          {!activeService ? (
            <div className="text-center py-5">
              <i className="fas fa-hand-pointer fa-3x text-muted mb-3"></i>
              <p className="text-muted">Sélectionnez un service pour commencer</p>
            </div>
          ) : (
            <>
              {renderParameterInputs()}

              <div className="d-flex gap-2 mb-3">
                <button
                  className="btn btn-primary"
                  onClick={handleExecuteService}
                  disabled={
                    loading || 
                    (activeService === 'createProductPortal' && (
                      !productImageUrl || 
                      !productName || 
                      productName.length > 150 ||
                      !productDescription || 
                      productDescription.length > 250 ||
                      !productPrice || 
                      parseFloat(productPrice) < 5 ||
                      selectedCatalogs.length === 0 ||
                      !selectedCategoryId
                    ))
                  }
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Exécution...
                    </>
                  ) : (
                    'Exécuter le service'
                  )}
                </button>
                <button
                  className="btn btn-outline-secondary"
                  onClick={resetForm}
                  disabled={loading}
                >
                  Réinitialiser
                </button>
              </div>

              {/* Results */}
              {error && (
                <div className="alert alert-danger" role="alert">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  {error}
                </div>
              )}

              {/* Sync All Categories Progress */}
              {isSyncingAllCategories && syncAllProgress.current > 0 && (
                <div className="card mb-3">
                  <div className="card-header bg-info text-white">
                    <i className="fas fa-sync-alt fa-spin me-2"></i>
                    Synchronisation en cours...
                  </div>
                  <div className="card-body">
                    <div className="progress mb-3" style={{ height: '25px' }}>
                      <div
                        className="progress-bar progress-bar-striped progress-bar-animated"
                        role="progressbar"
                        style={{ width: `${(syncAllProgress.current / syncAllProgress.total) * 100}%` }}
                        aria-valuenow={syncAllProgress.current}
                        aria-valuemin="0"
                        aria-valuemax={syncAllProgress.total}
                      >
                        {syncAllProgress.current} / {syncAllProgress.total}
                      </div>
                    </div>
                    {syncAllProgress.results.length > 0 && (
                      <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                        <table className="table table-sm">
                          <thead>
                            <tr>
                              <th>Catégorie</th>
                              <th>Statut</th>
                            </tr>
                          </thead>
                          <tbody>
                            {syncAllProgress.results.map((result, idx) => (
                              <tr key={idx}>
                                <td>{result.category}</td>
                                <td>
                                  {result.success ? (
                                    <span className="badge bg-success">
                                      <i className="fas fa-check me-1"></i>Réussi
                                    </span>
                                  ) : (
                                    <span className="badge bg-danger" title={result.error}>
                                      <i className="fas fa-times me-1"></i>Échoué
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Sync All Categories Final Results */}
              {!isSyncingAllCategories && syncAllProgress.results.length > 0 && (
                <div className="card mb-3">
                  <div className="card-header bg-success text-white">
                    <i className="fas fa-check-circle me-2"></i>
                    Résultats de la synchronisation ({syncAllProgress.results.length} catégories)
                  </div>
                  <div className="card-body">
                    <div className="row mb-3">
                      <div className="col-md-4">
                        <div className="text-center p-3 bg-success rounded">
                          <h3 className="mb-0 text-white">
                            {syncAllProgress.results.filter(r => r.success).length}
                          </h3>
                          <small className="text-white">Réussi</small>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="text-center p-3 bg-danger rounded">
                          <h3 className="mb-0 text-white">
                            {syncAllProgress.results.filter(r => !r.success).length}
                          </h3>
                          <small className="text-white">Échoué</small>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="text-center p-3 bg-primary rounded">
                          <h3 className="mb-0 text-white">
                            {syncAllProgress.results.length}
                          </h3>
                          <small className="text-white">Total</small>
                        </div>
                      </div>
                    </div>
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      <table className="table table-sm table-hover">
                        <thead className="table-light sticky-top">
                          <tr>
                            <th>Catégorie</th>
                            <th>Category ID</th>
                            <th>Statut</th>
                            <th>Détails</th>
                          </tr>
                        </thead>
                        <tbody>
                          {syncAllProgress.results.map((result, idx) => (
                            <tr key={idx}>
                              <td>{result.category}</td>
                              <td><code>{result.categoryId}</code></td>
                              <td>
                                {result.success ? (
                                  <span className="badge bg-success">
                                    <i className="fas fa-check me-1"></i>Réussi
                                  </span>
                                ) : (
                                  <span className="badge bg-danger">
                                    <i className="fas fa-times me-1"></i>Échoué
                                  </span>
                                )}
                              </td>
                              <td>
                                {result.success ? (
                                  <small className="text-muted">
                                    {result.data?.productsAdded || 0} produits ajoutés,{' '}
                                    {result.data?.productsUpdated || 0} mis à jour
                                  </small>
                                ) : (
                                  <small className="text-danger">{result.error}</small>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="mt-3">
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => setSyncAllProgress({ current: 0, total: 0, results: [] })}
                      >
                        <i className="fas fa-times me-1"></i>
                        Effacer les résultats
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Orders Sync Results */}
              {ordersSyncResult && (
                <div className="card mb-3">
                  <div className="card-header bg-info text-white">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <i className="fas fa-shopping-cart me-2"></i>
                        Résultats de la synchronisation des commandes
                      </div>
                      <div className="form-check form-switch mb-0">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="autoSyncToggle"
                          checked={isAutoSyncEnabled}
                          onChange={(e) => setIsAutoSyncEnabled(e.target.checked)}
                        />
                        <label className="form-check-label text-white" htmlFor="autoSyncToggle">
                          Auto-sync (5 min)
                        </label>
                      </div>
                    </div>
                    {lastSyncTime && (
                      <small className="text-white-50">
                        Dernière sync: {lastSyncTime.toLocaleString()}
                      </small>
                    )}
                  </div>
                  <div className="card-body">
                    <div className="row mb-3">
                      <div className="col-md-3">
                        <div className="text-center p-3 bg-primary rounded">
                          <h3 className="mb-0 text-white">
                            {ordersSyncResult.totalOrders || 0}
                          </h3>
                          <small className="text-white">Total commandes</small>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="text-center p-3 bg-success rounded">
                          <h3 className="mb-0 text-white">
                            {ordersSyncResult.updated || 0}
                          </h3>
                          <small className="text-white">Mises à jour</small>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="text-center p-3 bg-warning rounded">
                          <h3 className="mb-0 text-white">
                            {ordersSyncResult.notFound?.length || 0}
                          </h3>
                          <small className="text-white">Non trouvées</small>
                        </div>
                      </div>
                      <div className="col-md-3">
                        <div className="text-center p-3 bg-danger rounded">
                          <h3 className="mb-0 text-white">
                            {ordersSyncResult.errors?.length || 0}
                          </h3>
                          <small className="text-white">Erreurs</small>
                        </div>
                      </div>
                    </div>

                    {ordersSyncResult.vendorCodes && ordersSyncResult.vendorCodes.length > 0 && (
                      <div className="mb-3">
                        <strong>Magasins synchronisés:</strong>{' '}
                        <span className="badge bg-secondary ms-1">{ordersSyncResult.vendorCodes.join(', ')}</span>
                      </div>
                    )}

                    {ordersSyncResult.alreadyGone && ordersSyncResult.alreadyGone.length > 0 && (
                      <div className="alert alert-info">
                        <strong>Déjà passées ({ordersSyncResult.alreadyGone.length}):</strong>{' '}
                        {ordersSyncResult.alreadyGone.join(', ')}
                      </div>
                    )}

                    {ordersSyncResult.notFound && ordersSyncResult.notFound.length > 0 && (
                      <div className="alert alert-warning">
                        <strong>Non trouvées ({ordersSyncResult.notFound.length}):</strong>{' '}
                        {ordersSyncResult.notFound.join(', ')}
                      </div>
                    )}

                    {ordersSyncResult.errors && ordersSyncResult.errors.length > 0 && (
                      <div className="alert alert-danger">
                        <strong>Erreurs:</strong>
                        <ul className="mb-0 mt-2">
                          {ordersSyncResult.errors.map((err, idx) => (
                            <li key={idx}>{err}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <button
                      className="btn btn-sm btn-outline-secondary mt-2"
                      onClick={() => setOrdersSyncResult(null)}
                    >
                      <i className="fas fa-times me-1"></i>
                      Effacer les résultats
                    </button>
                  </div>
                </div>
              )}

              {result && (
                <>
                  {/* Display categories in table if available */}
                  {result.categories && Array.isArray(result.categories) && result.categories.length > 0 ? (
                    <div>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="mb-0">
                          <i className="fas fa-folder me-2"></i>
                          Catégories ({result.categories.length})
                        </h6>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => setResult({ ...result, showJson: !result.showJson })}
                        >
                          {result.showJson ? 'Afficher tableau' : 'Afficher JSON'}
                        </button>
                      </div>

                      {result.showJson ? (
                        <div className="card">
                          <div className="card-header bg-success text-white">
                            <i className="fas fa-check-circle me-2"></i>
                            Résultat JSON
                          </div>
                          <div className="card-body">
                            <pre className="bg-light p-3 rounded" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                              {JSON.stringify(result, null, 2)}
                            </pre>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="table-responsive">
                            <table className="table table-hover">
                              <thead className="table-light">
                                <tr>
                                  <th>Nom</th>
                                  <th>Remote ID</th>
                                  <th>Catalog ID</th>
                                  <th>Store ID</th>
                                  <th>Status</th>
                                  <th>Produits</th>
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {result.categories
                                  .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                                  .map((category, index) => (
                                    <tr key={category._id || category.id || index}>
                                      <td>
                                        <strong>{category.name}</strong>
                                        {category.description && (
                                          <small className="d-block text-muted">{category.description}</small>
                                        )}
                                      </td>
                                      <td><code className="small">{category.id || category.remote_plateform_id || 'N/A'}</code></td>
                                      <td><code className="small">{category._catalogId || category.catalog_id || category.catalogId || 'N/A'}</code></td>
                                      <td><code className="small">{category.store_id || 'N/A'}</code></td>
                                      <td>
                                        {category.enable !== false ? (
                                          <span className="badge bg-success">Activé</span>
                                        ) : (
                                          <span className="badge bg-secondary">Désactivé</span>
                                        )}
                                      </td>
                                      <td>
                                        {category.productsCount !== undefined ? (
                                          <span className="badge bg-info">{category.productsCount} produits</span>
                                        ) : category.totalProductCount !== undefined ? (
                                          <span className="badge bg-info">{category.totalProductCount} produits</span>
                                        ) : (
                                          <span className="text-muted">-</span>
                                        )}
                                      </td>
                                      <td>
                                        <button
                                          className="btn btn-sm btn-primary"
                                          onClick={() => handleFetchProductsForCategory(category)}
                                          disabled={fetchingProductsForCategory === (category.id || category.remote_plateform_id)}
                                        >
                                          {fetchingProductsForCategory === (category.id || category.remote_plateform_id) ? (
                                            <>
                                              <span className="spinner-border spinner-border-sm me-1"></span>
                                              Chargement...
                                            </>
                                          ) : (
                                            <>
                                              <i className="fas fa-download me-1"></i>
                                              Obtenir produits
                                            </>
                                          )}
                                        </button>
                                      </td>
                                    </tr>
                                  ))}
                              </tbody>
                            </table>
                          </div>
                          
                          {/* Pagination */}
                          {result.categories.length > itemsPerPage && (
                            <nav>
                              <ul className="pagination pagination-sm justify-content-center">
                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                  <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(currentPage - 1)}
                                    disabled={currentPage === 1}
                                  >
                                    Précédent
                                  </button>
                                </li>
                                {Array.from({ length: Math.ceil(result.categories.length / itemsPerPage) }, (_, i) => i + 1).map(page => (
                                  <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                                    <button
                                      className="page-link"
                                      onClick={() => setCurrentPage(page)}
                                    >
                                      {page}
                                    </button>
                                  </li>
                                ))}
                                <li className={`page-item ${currentPage === Math.ceil(result.categories.length / itemsPerPage) ? 'disabled' : ''}`}>
                                  <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(currentPage + 1)}
                                    disabled={currentPage === Math.ceil(result.categories.length / itemsPerPage)}
                                  >
                                    Suivant
                                  </button>
                                </li>
                              </ul>
                            </nav>
                          )}
                        </>
                      )}
                    </div>
                  ) : result.products && Array.isArray(result.products) && result.products.length > 0 ? (
                    /* Display products in cards if available */
                    <div>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="mb-0">
                          <i className="fas fa-box me-2"></i>
                          Produits ({result.totalProducts || result.products.length})
                        </h6>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => setResult({ ...result, showJson: !result.showJson })}
                        >
                          {result.showJson ? 'Afficher cartes' : 'Afficher JSON'}
                        </button>
                      </div>

                      {result.showJson ? (
                        <div className="card">
                          <div className="card-header bg-success text-white">
                            <i className="fas fa-check-circle me-2"></i>
                            Résultat JSON
                          </div>
                          <div className="card-body">
                            <pre className="bg-light p-3 rounded" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                              {JSON.stringify(result, null, 2)}
                            </pre>
                          </div>
                        </div>
                      ) : (
                        <div className="row g-3">
                          {result.products.map((product, index) => (
                            <div key={product.id || index} className="col-md-4">
                              <div className="card h-100">
                                {product.images && product.images.length > 0 && (
                                  <img
                                    src={product.images[0].url}
                                    className="card-img-top"
                                    alt={product.name}
                                    style={{ height: '200px', objectFit: 'cover' }}
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                )}
                                <div className="card-body">
                                  <h6 className="card-title text-truncate" title={product.name}>
                                    {product.name}
                                  </h6>
                                  {product.description && (
                                    <p className="card-text small text-muted" style={{ 
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical'
                                    }}>
                                      {product.description}
                                    </p>
                                  )}
                                  <div className="d-flex justify-content-between align-items-center mb-2">
                                    <strong className="text-primary">
                                      {product.unitPrice ? `${product.unitPrice} ${product.currency || 'MAD'}` : 'N/A'}
                                    </strong>
                                    {product.availability ? (
                                      product.availability.available ? (
                                        <span className="badge bg-success">Disponible</span>
                                      ) : (
                                        <span className="badge bg-secondary">Indisponible</span>
                                      )
                                    ) : null}
                                  </div>
                                  {product.category && (
                                    <small className="text-muted d-block">
                                      <i className="fas fa-folder me-1"></i>
                                      {product.category.name}
                                    </small>
                                  )}
                                  <small className="text-muted d-block">
                                    <i className="fas fa-barcode me-1"></i>
                                    ID: {product.id}
                                  </small>
                                  {product.productOptionIds && product.productOptionIds.length > 0 && (
                                    <small className="text-muted d-block">
                                      <i className="fas fa-puzzle-piece me-1"></i>
                                      {product.productOptionIds.length} option(s)
                                    </small>
                                  )}
                                </div>
                                <div className="card-footer bg-transparent p-2">
                                  <div className="d-flex gap-2">
                                    <button
                                      className={`btn btn-sm flex-grow-1 ${product.availability?.available ? 'btn-outline-danger' : 'btn-outline-success'}`}
                                      onClick={() => handleToggleProduct(product)}
                                      disabled={loading}
                                    >
                                      <i className={`fas ${product.availability?.available ? 'fa-eye-slash' : 'fa-eye'} me-1`}></i>
                                      {product.availability?.available ? 'Désactiver' : 'Activer'}
                                    </button>
                                    <button
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() => handleDeleteProduct(product)}
                                      disabled={loading}
                                      title="Supprimer le produit"
                                    >
                                      <i className="fas fa-trash"></i>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    /* Show JSON for non-product results */
                    <div className="card">
                      <div className="card-header bg-success text-white">
                        <i className="fas fa-check-circle me-2"></i>
                        Résultat
                      </div>
                      <div className="card-body">
                        <pre className="bg-light p-3 rounded" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                          {JSON.stringify(result, null, 2)}
                        </pre>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
      
      {/* Products Modal */}
      {showProductsModal && (
        <div 
          className="modal fade show" 
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={(e) => {
            if (e.target.className.includes('modal')) {
              setShowProductsModal(false);
            }
          }}
        >
          <div className="modal-dialog modal-xl modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-box me-2"></i>
                  Produits - {modalCategoryName}
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowProductsModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                {modalProducts.length === 0 ? (
                  <div className="alert alert-info">
                    <i className="fas fa-info-circle me-2"></i>
                    Aucun produit trouvé dans cette catégorie
                  </div>
                ) : (
                  <div className="row g-3">
                    {modalProducts.map((product, index) => (
                      <div key={product.id || index} className="col-md-4">
                        <div className="card h-100">
                          {product.images && product.images.length > 0 && (
                            <img
                              src={product.images[0].url}
                              className="card-img-top"
                              alt={product.name}
                              style={{ height: '200px', objectFit: 'cover' }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          )}
                          <div className="card-body">
                            <h6 className="card-title text-truncate" title={product.name}>
                              {product.name}
                            </h6>
                            {product.description && (
                              <p className="card-text small text-muted" style={{ 
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical'
                              }}>
                                {product.description}
                              </p>
                            )}
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <strong className="text-primary">
                                {product.unitPrice ? `${product.unitPrice} ${product.currency || 'MAD'}` : 'N/A'}
                              </strong>
                              {product.availability ? (
                                product.availability.available ? (
                                  <span className="badge bg-success">Disponible</span>
                                ) : (
                                  <span className="badge bg-secondary">Indisponible</span>
                                )
                              ) : null}
                            </div>
                            {product.category && (
                              <small className="text-muted d-block">
                                <i className="fas fa-folder me-1"></i>
                                {product.category.name}
                              </small>
                            )}
                            <small className="text-muted d-block">
                              <i className="fas fa-barcode me-1"></i>
                              ID: {product.id}
                            </small>
                            {product.product_id && (
                              <small className="text-success d-block">
                                <i className="fas fa-database me-1"></i>
                                Local ID: {product.product_id}
                              </small>
                            )}
                            {product.productOptionIds && product.productOptionIds.length > 0 && (
                              <small className="text-muted d-block">
                                <i className="fas fa-puzzle-piece me-1"></i>
                                {product.productOptionIds.length} option(s)
                              </small>
                            )}
                          </div>
                          <div className="card-footer bg-transparent p-2">
                            <div className="d-flex gap-2">
                              <button
                                className={`btn btn-sm flex-grow-1 ${product.availability?.available ? 'btn-outline-danger' : 'btn-outline-success'}`}
                                onClick={() => handleToggleProduct(product)}
                                disabled={loading}
                              >
                                <i className={`fas ${product.availability?.available ? 'fa-eye-slash' : 'fa-eye'} me-1`}></i>
                                {product.availability?.available ? 'Désactiver' : 'Activer'}
                              </button>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleDeleteProduct(product)}
                                disabled={loading}
                                title="Supprimer le produit"
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowProductsModal(false)}
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Image Crop Modal */}
      {showCropModal && imageToCrop && (
        <SquareImageCropModal
          imageUrl={imageToCrop}
          onCropComplete={handleCroppedImage}
          onCancel={() => {
            setShowCropModal(false);
            setImageToCrop(null);
            setProductImageFile(null);
          }}
        />
      )}
    </div>
  );
};

export default GlovoSistersServiceManager;
